import {
  Alert,
  Button,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import { useAuth } from "@poseidon/ui/components/AuthContext";
import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useToastMessage } from "@poseidon/ui/app/toasts/useToastMessage";
import {
  FormikDisabler,
  Panel,
  usePermissions,
} from "@poseidon/ui/components";
import Form from "@poseidon/ui/components/form/FullWidthForm";
import { Field, Formik } from "formik";
import { RadioGroup } from "formik-mui";
import { FileManagement } from "../../../Files";
import { AffordabilityCalculator } from "./AffordabilityCalculator";
import { ReasonDialog } from "../../components/ReasonDialog";
import * as api from "../../api";
import NoteLogPanel from "../../../NoteLog/NoteLogPanel";
import { useCreditUnderwriting } from "../../hooks/useCreditUnderwriting";
import config from "@poseidon/utils/config";
import { useApplicationContext } from "../edit/ApplicationContext";
import { ApplicationHasAgreementWarning } from "../../components/ApplicationHasAgreementWarning";
import { isApplicationEditable } from "../../isApplicationEditable";
import React, { useState } from "react";
import DatePicker from "@poseidon/ui/components/form/DatePicker";
import { getDate } from "@poseidon/firebase/timestamp";

const noteLogMapper = (doc) => ({
  ...doc.data(),
  id: doc.id,
  createdAt: getDate(doc.get("createdAt")),
});

export const CreditUnderwritingSection = () => {
  const {
    getFileResolver,
    riskStatusOptions,
    passFailOptions,
    declineReasonOptions,
    getDeclineReasonLabel,
    noteCategories,
  } = config;

  const { canWrite } = usePermissions();
  const { user } = useAuth();
  const { setError } = useErrorMessage();
  const { setToast } = useToastMessage();
  const application = useApplicationContext();
  const [saving, setSaving] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);

  const { creditUnderwriting, loading, error } = useCreditUnderwriting(
    application.id
  );

  const addNoteHandler =
    ({ message, noteCategory }) =>
    (values, { resetForm }) => {
      try {
        api.addNote({
          ...values,
          application,
          noteCategory,
        });
        resetForm();
        setToast(message);
      } catch (error) {
        setError(error.message);
      }
    };

  const onCreditReportNotesAddHandler = addNoteHandler({
    message: "Added Credit Report Note",
    noteCategory: noteCategories.creditReportNote,
  });

  const onAffordabilityNotesAddHandler = addNoteHandler({
    message: "Added Affordability Note",
    noteCategory: noteCategories.affordabilityNote,
  });

  const onCreditSummaryNotesAddHandler = addNoteHandler({
    message: "Added Application Summary Note",
    noteCategory: noteCategories.creditSummaryNote,
  });

  const onSubmitHandler = async (values) => {
    try {
      setSaving(true);
      await api.updateCreditUnderwriting(application.id, values);
      setToast("Updated Credit Underwriting");
    } catch (error) {
      setError(error.message);
    }
    setSaving(false);
  };

  const onDeclineConfirm = async ({ declineReason, declineReasonDetail }) => {
    try {
      setSaving(true);
      await api.declineApplication(application.id, {
        declineReason,
        declineReasonDetail,
        user,
      });
      setDeclineDialogOpen(false);
      setToast("Application declined");
    } catch (error) {
      setError(error.message);
    }
    setSaving(false);
  };

  if (error) {
    setError(error);
  }

  if (loading || !creditUnderwriting) {
    return <CircularProgress sx={{ alignSelf: "center" }} />;
  }
  const readonly = !isApplicationEditable(application);

  const {
    creditReportStatus,
    creditSummaryStatus,
    creditSummaryStatusExpiry,
    declineReason,
    declineReasonDetail,
    declineLetters,
  } = creditUnderwriting;
  const declined = creditSummaryStatus === "fail";

  return (
    <Stack spacing={5} sx={{ mb: 5, mx: 5 }}>
      <ApplicationHasAgreementWarning application={application} />

      <Panel title="File Upload">
        <FileManagement
          fileCategory="application"
          resolveMetadata={getFileResolver("creditUnderwriting")}
          applicationId={application.id}
          readonly={readonly}
        />
      </Panel>

      <NoteLogPanel
        title="Credit Report Notes"
        dbPathSlugs={[
          "applications",
          application.id,
          noteCategories.creditReportNote,
        ]}
        updateHandler={onCreditReportNotesAddHandler}
        mapper={noteLogMapper}
      />

      <Panel title="Credit Report">
        <Formik
          initialValues={{
            creditReportStatus: creditReportStatus ?? "pending",
          }}
          enableReinitialize
          onSubmit={onSubmitHandler}
        >
          {({ dirty, isSubmitting }) => (
            <Form>
              <FormikDisabler disabled={readonly || saving || !canWrite} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field component={RadioGroup} name="creditReportStatus">
                    {riskStatusOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio disabled={isSubmitting} />}
                        label={option.label}
                        disabled={isSubmitting}
                      />
                    ))}
                  </Field>
                </Grid>

                {dirty && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      margin="normal"
                    >
                      Save Status
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </Panel>

      <NoteLogPanel
        title="Affordability Notes"
        dbPathSlugs={[
          "applications",
          application.id,
          noteCategories.affordabilityNote,
        ]}
        updateHandler={onAffordabilityNotesAddHandler}
        mapper={noteLogMapper}
      />

      <AffordabilityCalculator application={application} />

      <NoteLogPanel
        title="Application Summary Notes"
        dbPathSlugs={[
          "applications",
          application.id,
          noteCategories.creditSummaryNote,
        ]}
        updateHandler={onCreditSummaryNotesAddHandler}
        mapper={noteLogMapper}
      />

      <Panel title="Application Summary">
        <Formik
          initialValues={{
            creditSummaryStatus: creditSummaryStatus ?? "pending",
            creditSummaryStatusExpiry: creditSummaryStatusExpiry ?? null,
          }}
          enableReinitialize
          onSubmit={(values) =>
            // approving or resetting to pending clears any earlier decline
            onSubmitHandler({
              ...values,
              declineReason: null,
              declineReasonDetail: null,
            })
          }
        >
          {({ dirty, isSubmitting, values, setFieldValue }) => (
            <Form>
              <FormikDisabler disabled={readonly || saving || !canWrite} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    component={RadioGroup}
                    name="creditSummaryStatus"
                    onChange={(ev, value) => {
                      if (value === "fail") {
                        // the reason is captured in the dialog, which saves
                        // the decline itself; the radio stays as it was
                        setDeclineDialogOpen(true);
                        return;
                      }
                      setFieldValue("creditSummaryStatus", value);
                      if (value === "pass") {
                        const d = new Date();
                        d.setMonth(d.getMonth() + 3);
                        setFieldValue("creditSummaryStatusExpiry", d);
                      } else {
                        setFieldValue("creditSummaryStatusExpiry", null);
                      }
                    }}
                  >
                    {passFailOptions.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio disabled={isSubmitting} />}
                        label={option.label}
                        disabled={isSubmitting}
                      />
                    ))}
                  </Field>
                </Grid>
                <Grid item xs={12}>
                  <Field
                    component={DatePicker}
                    name="creditSummaryStatusExpiry"
                    label="Decision Expiry"
                    margin="normal"
                    disabled={values.creditSummaryStatus !== "pass"}
                  />
                </Grid>
                {declined && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      <Typography variant="body2">
                        <strong>Declined:</strong>{" "}
                        {getDeclineReasonLabel(declineReason) ??
                          "no reason recorded"}
                        {declineReasonDetail ? ` — ${declineReasonDetail}` : ""}
                      </Typography>
                      <Typography variant="body2">
                        {declineLetters?.length
                          ? "Decline letter attached — see File Upload above."
                          : "Decline letter is being generated and will appear under File Upload above."}
                      </Typography>
                    </Alert>
                  </Grid>
                )}
                {dirty && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      margin="normal"
                    >
                      Save Status
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Form>
          )}
        </Formik>
      </Panel>

      <ReasonDialog
        open={declineDialogOpen}
        title="Decline application"
        description="Choose the reason for declining. The application will be marked as rejected and the decline letter will be generated and attached to the application."
        options={declineReasonOptions}
        confirmLabel="Decline application"
        onClose={() => setDeclineDialogOpen(false)}
        onConfirm={({ reason, detail }) =>
          onDeclineConfirm({ declineReason: reason, declineReasonDetail: detail })
        }
        saving={saving}
      />
    </Stack>
  );
};
